import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { getBlog, BLOGS } from "../lib/blogs";

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlog(slug);

  if (!post) {
    return (
      <div
        data-testid="blog-not-found"
        className="pt-40 pb-40 max-w-[1400px] mx-auto px-6 md:px-10 text-center"
      >
        <h1 className="font-serif text-5xl text-[#2C3C30]">Article not found</h1>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 mt-8 font-sans text-[#2C3C30] uppercase tracking-wide text-sm border-b border-[#2C3C30]/40 pb-1"
        >
          <ArrowLeft size={16} /> Back to journal
        </Link>
      </div>
    );
  }

  const related = BLOGS.filter((b) => b.slug !== post.slug).slice(0, 3);

  return (
    <article data-testid="blog-post-page" className="pt-28 md:pt-36 pb-24 md:pb-40">
      <div className="max-w-[820px] mx-auto px-6 md:px-10">
        <Link
          to="/blog"
          data-testid="back-to-blog"
          className="inline-flex items-center gap-2 font-sans text-sm uppercase tracking-wide text-[#566158] hover:text-[#2C3C30] transition-colors"
        >
          <ArrowLeft size={16} /> Journal
        </Link>

        <span className="block mt-10 font-sans text-xs uppercase tracking-[0.25em] text-[#7D8C7A]">
          {post.category}
        </span>
        <h1 className="font-serif text-4xl md:text-6xl text-[#2C3C30] tracking-tight leading-[1.02] mt-5">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 mt-6 font-sans text-sm text-[#566158]">
          <span>{post.author}</span>
          <span>&middot;</span>
          <span>{formatDate(post.date)}</span>
          <span>&middot;</span>
          <span>{post.readTime}</span>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-12">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl overflow-hidden"
        >
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-[320px] md:h-[520px] object-cover"
          />
        </motion.div>
      </div>

      <div className="max-w-[820px] mx-auto px-6 md:px-10 mt-14">
        {post.content.map((block, i) =>
          block.type === "h" ? (
            <Reveal key={i}>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2C3C30] tracking-tight mt-12 mb-4">
                {block.text}
              </h2>
            </Reveal>
          ) : (
            <Reveal key={i}>
              <p className="font-sans text-lg text-[#3a423c] leading-[1.85] mb-6">
                {block.text}
              </p>
            </Reveal>
          )
        )}

        <div className="mt-16 p-8 md:p-10 rounded-3xl bg-[#2C3C30] text-[#F7F6F2]">
          <p className="font-serif text-2xl md:text-3xl tracking-tight leading-snug">
            Have questions about your own gut health?
          </p>
          <Link
            to="/contact"
            data-testid="blog-cta"
            className="group inline-flex items-center gap-3 mt-6 rounded-full bg-[#F7F6F2] px-7 py-3.5 text-[#2C3C30] font-medium transition-colors duration-300 hover:bg-[#D1D6CC]"
          >
            Book a Consultation
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </div>
      </div>

      {/* Related */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
        <h3 className="font-serif text-4xl text-[#2C3C30] tracking-tight mb-10">
          Keep reading
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/blog/${r.slug}`}
              className="group flex flex-col"
            >
              <div className="overflow-hidden rounded-2xl">
                <motion.img
                  src={r.image}
                  alt={r.title}
                  className="w-full h-[200px] object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#7D8C7A] mt-5">
                {r.category}
              </span>
              <h4 className="font-serif text-2xl text-[#2C3C30] tracking-tight mt-2 group-hover:text-[#7D8C7A] transition-colors duration-300">
                {r.title}
              </h4>
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}
